/* eslint-disable @typescript-eslint/no-unused-vars */
import { Injectable } from '@angular/core';
import { delay, Observable, of } from 'rxjs';
import { Assignee, ChangeType, Task, TaskComment, TaskDetails, TaskHistory } from '../models/task';
import { WorkspaceTaskService } from '../services/task.service';

const MOCK_ASSIGNEES: Assignee[] = [
  { id: 'u1', name: 'Alex Johnson', type: 'user' },
  { id: 'u2', name: 'Sarah Lee', type: 'user' },
  { id: 'u3', name: 'David Chen', type: 'user' },
  { id: 'p1', name: 'Review Agent', type: 'persona' },
  { id: 'p2', name: 'Data Analyst Bot', type: 'persona' }
];

const TODAY = new Date();
const TOMORROW = new Date(TODAY);
TOMORROW.setDate(TODAY.getDate() + 1);
const getRandomInt = (min: number, max: number): number => Math.floor(Math.random() * (max - min)) + min;

const generateRandomDatePair = () => {
  const currentYear = 2025;
  const startOfYear = new Date(currentYear, 10, Math.floor(Math.random() * 30));

  const createdDate = new Date(startOfYear);
  const randomDaysToAdd = getRandomInt(1, 41);

  const dueDate = new Date(createdDate);
  dueDate.setDate(createdDate.getDate() + randomDaysToAdd);

  return {
    created_at: createdDate.getTime(),
    updated_at: createdDate.getTime(),
    due_date: dueDate.toISOString()
  };
};
const MOCK_TASKS: Task[] = [
  {
    task_id: 't-101',
    name: 'Draft Project Proposal',
    description: 'Create the initial draft including scope, budget, and timeline.',
    assigned_to: MOCK_ASSIGNEES[0],
    created_by: MOCK_ASSIGNEES[3],
    due_date: TOMORROW.toISOString(),
    created_at: new Date(2025, 9, 10).toISOString(),

    status: 'todo',
    priority: 1,
    tags: []
  },
  {
    task_id: 't-102',
    name: 'Review Client Feedback',
    description: 'Go through the latest feedback document and tag required changes.',
    assigned_to: MOCK_ASSIGNEES[1],
    created_by: MOCK_ASSIGNEES[0],
    created_at: new Date(2025, 9, 10).toISOString(),

    due_date: TOMORROW.toISOString(),
    status: 'todo',
    priority: 1,
    tags: []
  },
  {
    task_id: 't-201',
    name: 'Analyze Q3 Sales Data',
    description: 'Run pivot tables and identify top 5 performing regions.',
    assigned_to: MOCK_ASSIGNEES[4],
    created_by: MOCK_ASSIGNEES[1],
    due_date: new Date(2025, 10, 15).toISOString(),
    created_at: new Date(2025, 9, 10).toISOString(),

    status: 'in_progress',
    priority: 1,
    tags: []
  },
  {
    task_id: 't-202',
    name: 'Update Marketing Copy (Inline)',
    description: 'Refine headline and opening paragraph based on A/B test results.',
    assigned_to: MOCK_ASSIGNEES[2],
    created_by: MOCK_ASSIGNEES[3],
    due_date: TOMORROW.toISOString(),
    created_at: new Date(2025, 9, 10).toISOString(),
    status: 'in_progress',
    priority: 1,
    tags: []
  },
  {
    task_id: 't-301',
    name: 'Submitted Expense Report',
    description: 'All receipts uploaded and submitted via the internal portal.',
    assigned_to: MOCK_ASSIGNEES[0],
    created_by: MOCK_ASSIGNEES[0],
    due_date: new Date(2025, 9, 20).toISOString(),
    status: 'done',
    priority: 1,
    tags: [],
    created_at: new Date(2025, 9, 10).toISOString()
  }
].map(task => {
  const randomDates = generateRandomDatePair();
  return {
    ...task,
    ...randomDates
  } as Task satisfies Task;
});

const mockHistory: TaskHistory[] = [
  {
    history_id: 'hist-001',
    change_type: 'created' as ChangeType,
    changed_by: MOCK_ASSIGNEES[0],
    changes: {
      name: { old: '', new: 'Initial Feature Refactoring' }
    },
    created_at: new Date(Date.now() - 604800000).toISOString()
  }
];
const mockComment: TaskComment[] = [
  {
    comment_id: 'cmt-001',
    created_at: 0,
    updated_at: 0,
    comment: 'I believe we should prioritize the database refactoring task this sprint. It has a high impact.',
    created_by: MOCK_ASSIGNEES[0]
  },
  {
    comment_id: 'cmt-002',
    created_at: 0,
    updated_at: 0,
    comment:
      "Agreed, I've adjusted the priority to 5 and assigned it to the team. **Update:** I fixed a typo in the description.",
    created_by: MOCK_ASSIGNEES[0]
  },
  {
    comment_id: 'cmt-003',
    created_at: 0,
    updated_at: 0,
    comment: 'Could someone check the acceptance criteria? They seem a bit vague.',
    created_by: MOCK_ASSIGNEES[0]
  }
];

export const mockTaskDetails: TaskDetails = {
  task_id: 'TASK-042',
  name: 'Implement Task Detail Dialog History Tabs',
  description:
    'Update the task detail dialog to include a history section tracking status and assignee changes, and a comments section for collaborative notes.',
  status: 'done',
  assigned_to: MOCK_ASSIGNEES[0],
  created_by: MOCK_ASSIGNEES[0],
  due_date: new Date(2025, 10, 31).toISOString(),
  priority: 1,
  tags: [],
  history: mockHistory,
  comment: mockComment,
  created_at: new Date(2025, 9, 31).getTime(),
  updated_at: new Date(2025, 9, 31).getTime()
};

@Injectable()
export class MockWorkspaceTaskService extends WorkspaceTaskService {
  public override getTasks(workspaceId: string): Observable<Task[]> {
    return of(MOCK_TASKS).pipe(delay(500));
  }

  public override getTaskById(taskId: string): Observable<TaskDetails> {
    return of({ ...mockTaskDetails, id: taskId }).pipe(delay(500));
  }
  public override getComments(workspaceId: string, taskId: string): Observable<TaskComment[]> {
    console.log(`Mock API Call: getComments for Task: ${taskId} in Workspace: ${workspaceId}`);
    return of(mockComment).pipe(delay(500));
  }

  public override getTaskHistory(workspaceId: string, taskId: string): Observable<TaskHistory[]> {
    console.log(`Mock API Call: getTaskHistory for Task: ${taskId} in Workspace: ${workspaceId}`);
    return of(mockHistory).pipe(delay(500));
  }
}
